import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, Toaster } from '@share/utils';
import { getHeaders, axiosInstanceB2B, getSelectedCurrency } from '@share/utils';
import { Urls } from '@share/constants';
import { ISavedProperties, ISavedPropertiesProcess } from '@common-types';

export interface ISavedPropertiesState {
  properties: ISavedProperties[];
  loading: boolean;
  process: ISavedPropertiesProcess;
  error: string;
}

const initialState: ISavedPropertiesState = {
  properties: [],
  loading: false,
  process: null,
  error: '',
};

const savedPropertiesSlice = createSlice({
  name: 'savedProperties',
  initialState,
  reducers: {
    setLoading: (state: ISavedPropertiesState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setProcess: (state: ISavedPropertiesState, { payload }: PayloadAction<ISavedPropertiesProcess>) => {
      state.process = payload;
    },
    setError: (state: ISavedPropertiesState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setProperties: (state: ISavedPropertiesState, { payload }: PayloadAction<ISavedProperties[]>) => {
      state.properties = payload;
    },
  },
});

export const savedPropertiesActions = savedPropertiesSlice.actions;
export const savedPropertiesReducer = savedPropertiesSlice.reducer;

export const getSavedProperties = (filter: string): AppThunk => {
  return async (dispatch) => {
    dispatch(savedPropertiesActions.setLoading(true));

    try {
      const response = await axiosInstanceB2B.get(`/identity/users/saved-property?orderBy=${filter}&sortOrder=desc`);
      dispatch(savedPropertiesActions.setProperties(response.data.result));
      dispatch(savedPropertiesActions.setLoading(false));
    } catch (error) {
      dispatch(savedPropertiesActions.setError(error.toString()));
      dispatch(savedPropertiesActions.setLoading(false));
    }
  };
};

export const handleSavedProperties = (property: ISavedProperties): AppThunk => {
  return async (dispatch) => {
    dispatch(savedPropertiesActions.setLoading(true));

    try {
      const response = await axiosInstanceB2B.post(`/identity/users/saved-property`, property);
      if (response.data.success) {
        const added = response.data.result.some(x => x.propertyId === property.propertyId);
        dispatch(savedPropertiesActions.setProcess({ id: property.propertyId, added: added }));
        dispatch(savedPropertiesActions.setProperties(response.data.result));
        if (added) {
          Toaster.success('Property saved successfully.');
        }
        else {
          Toaster.success('The property was removed.');
        }
      }
      dispatch(savedPropertiesActions.setLoading(false));
    } catch (error) {
      dispatch(savedPropertiesActions.setError(error.toString()));
      dispatch(savedPropertiesActions.setLoading(false));
    }
  };
};

export const deleteSavedProperties = (propertyId: number): AppThunk => {
  return async (dispatch) => {
    dispatch(savedPropertiesActions.setLoading(true));

    try {
      const response = await axiosInstanceB2B.post(`/identity/users/saved-property`, { propertyId });
      dispatch(savedPropertiesActions.setProperties(response.data.result));
      dispatch(savedPropertiesActions.setLoading(false));
      Toaster.success('The property was removed.');
    } catch (error) {
      dispatch(savedPropertiesActions.setError(error.toString()));
      dispatch(savedPropertiesActions.setLoading(false));
    }
  };
};

export const removeSavedProperties = (): AppThunk => {
  return async (dispatch) => {
    dispatch(savedPropertiesActions.setLoading(true));

    try {
      const response = await axiosInstanceB2B.delete(`/identity/users/saved-property`, {});
      if (response.data.success) {
        Toaster.success('The property was removed.');
        dispatch(savedPropertiesActions.setProperties(response.data.result));
      }
      dispatch(savedPropertiesActions.setLoading(false));
    } catch (error) {
      dispatch(savedPropertiesActions.setError(error.toString()));
      dispatch(savedPropertiesActions.setLoading(false));
    }
  };
};
